import React, {
  useState,
  useEffect,
  useContext,
  useMemo,
  useCallback,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import Cookies from "js-cookie";
import _get from "lodash/get";
import { Formik } from "formik";

import Button from "components/lib/Shared/Button";
import Tag from "components/lib/Shared/Tag";
import CommentsPopup from "components/lib/Global/CommentsPopup";
import ReactTable from "components/lib/Shared/ReactTable";
import SpinnerIcon from "components/lib/Shared/Icons/SpinnerIcon";
import { CheckIcon, PencilIcon, XIcon } from "@heroicons/react/outline";
import { INDIVIDUALS } from "components/PLCTABS/SFLdata/config/constants";
import { addComment, editComment, getComments } from "state/slices/comment";
import { resolveFields, invalidateFields } from "state/slices/resolveField";
import { Constants } from "config/constants";
import CommentsList from "components/lib/Global/CommentsList";
import ShareAllocationSelection from "./ShareAllocationSelection";
import { updateCompanyOfficialAsync } from "state/slices/tables/companyOfficialsSlice";
import DeleteButtonWithConfirmation from "components/lib/Shared/DeleteButtonWithConfirmation";
import ConfirmDialog from "components/lib/Shared/ConfirmDialog";
import OfficialsBeneficialOwnershipComponent from "../../../BeneficialOwnershipComponents/OfficialsBeneficialOwnershipComponent";
import UpdateCompanyDetailsContext from "context/UpdateCompanyDetails";
import MainShareAllocationComponent from "../../../ShareAllocationComponents";
import {
  calculateShareholdingPercentagesCrossTable,
  compareShareholdingPercentages,
  getShareholdersFromOfficials,
} from "utils/shareholdingCalculations";
import { handleCompanyMaintenanceFormData } from "utils/transactionRequestHandler";
import { getAllInitiateTransactionChangeAsync } from "state/slices/initiateTransactionChange";
import { getFullName } from "utils";

function OfficialShareAllocation({
  row,
  isViewMode,
  officialSliceName,
  companyOfficialEffectKey,
  officialShareAllocationComments,
  currentTab = "stakeholderTab",
  isChangesList,
  remainderAmounts,
  shareholderPercentageChanges, // NEW: From parent
  onPercentageChangesUpdate, // NEW: Callback to update parent
}) {
  const [showAllocateSharesBtn, setAllocateSharesBtn] = useState(true);
  const [editableShareAlloc, setEditableShareAlloc] = useState(null);

  // NEW: State for tracking percentage changes
  const [localPercentageChanges, setLocalPercentageChanges] = useState(null);
  const [showPercentageAlert, setShowPercentageAlert] = useState(false);

  // NEW: State for managing allocation completion (similar to ShareChangesSubRow)
  const [shareAllocationComplete, setShareAllocationComplete] = useState(false);
  const [isProcessingAllotment, setIsProcessingAllotment] = useState(false);

  // Track when we need to use fresh transaction data
  const [transactionDataVersion, setTransactionDataVersion] = useState(0);

  const { transactions, setRefechTransactionsOnUpdate } = useContext(
    UpdateCompanyDetailsContext
  );

  const dispatch = useDispatch();
  const history = useHistory();
  const currentUser = useSelector((state) => state.userSlice);
  const { company } = useSelector((state) => state.companySlice);
  const { modalRow } = useSelector((state) => state.modalsSlice);

  // Get all company officials for percentage calculations
  const originalCompanyOfficials = useSelector(
    (state) => state.companyOfficialsSlice.companyOfficials.items
  );

  // Handle Process Allotment for Changes List (updates transaction)
  // Similar to ShareChangesSubRow - embeds BO details in allocation within transaction
  const handleProcessAllotmentForChangesList = async (values) => {
    setIsProcessingAllotment(true);
    try {
      if (
        !transactions ||
        !Array.isArray(transactions) ||
        transactions.length === 0
      ) {
        throw new Error("No transaction found for this changes list item");
      }

      // Find the transaction for this row
      const matchingTransaction = transactions.find(
        (tx) =>
          tx._id === row.original.transaction ||
          tx.officials?.some((o) => o._id === row.original._id)
      );

      if (!matchingTransaction) {
        throw new Error("Transaction not found for this official");
      }

      const beneficialOwnershipValues = values.beneficialOwnershipForm || {};
      const officialId = row.original._id;
      const fullNameForAlloc =
        getFullName(row.original) ||
        row.original?.names?.companyName ||
        row.original.fullName ||
        "Unknown";

      // Get shareCapitalChanges from transaction
      const changes = Array.isArray(matchingTransaction?.shareCapitalChanges)
        ? [...matchingTransaction.shareCapitalChanges]
        : [];

      if (changes.length === 0) {
        throw new Error("No share capital changes found in transaction");
      }

      // Find the relevant share change (usually the first one for manual allotments)
      const targetIdx = changes.findIndex((change) =>
        change.allocations?.some(
          (alloc) => String(alloc.officialId) === String(officialId)
        )
      );

      if (targetIdx < 0) {
        throw new Error("Share change record not found for this official");
      }

      const change = changes[targetIdx];
      const existing = Array.isArray(change?.allocations)
        ? [...change.allocations]
        : [];
      let newAllocations = [...existing];
      const idx = newAllocations.findIndex(
        (a) => String(a?.officialId) === String(officialId)
      );

      // Get share allocation data from changesListData
      const shareAllocationData =
        getSharesFromTransactions(
          officialId,
          [change],
          nominalShareCapital
        )[0] || {};

      const allocatedShares = Number(shareAllocationData.allottedShares || 0);
      const currentShares = Number(shareAllocationData.currentShares || 0);
      const votingRights = Number(shareAllocationData.votingRights || 0);

      // Calculate proportion
      const proportionDenominator = Number(
        change?.sharesOnOffer || change?.onOffer || 0
      );
      const proportion =
        proportionDenominator > 0
          ? (allocatedShares / proportionDenominator).toFixed(6).toString()
          : "0.000000";

      // Prepare current and updated beneficial ownership details
      const originalBOForm = row?.original?.beneficialOwnershipForm || {};
      const currentBeneficialOwnership = {
        maxPercentShareholding: Number(
          originalBOForm.maxPercentShareholding || 0
        ),
        maxPercentVotingRights: Number(
          originalBOForm.maxPercentVotingRights || 0
        ),
        directPercentShareholding:
          originalBOForm.directPercentShareholding || "",
        indirectPercentShareholding:
          originalBOForm.indirectPercentShareholding || "",
        directPercentVotingRights:
          originalBOForm.directPercentVotingRights || "",
        indirectPercentVotingRights:
          originalBOForm.indirectPercentVotingRights || "",
        directRightRemoveDirector:
          originalBOForm.directRightRemoveDirector || "no",
        indirectRightRemoveDirector:
          originalBOForm.indirectRightRemoveDirector || "no",
        directCompanyControlRight:
          originalBOForm.directCompanyControlRight || "no",
        indirectCompanyControlRight:
          originalBOForm.indirectCompanyControlRight || "no",
        dateOfBecomingBO: originalBOForm.dateOfBecomingBO || "",
        sourceOfBOInformation: originalBOForm.sourceOfBOInformation || "",
        resolvedFields: row?.original?.resolvedFields || [],
      };

      const updatedBeneficialOwnership = {
        maxPercentShareholding:
          Number(beneficialOwnershipValues.maxPercentShareholding || 0) ||
          Number(beneficialOwnershipValues.directPercentShareholding || 0) +
            Number(beneficialOwnershipValues.indirectPercentShareholding || 0),
        maxPercentVotingRights:
          Number(beneficialOwnershipValues.maxPercentVotingRights || 0) ||
          Number(beneficialOwnershipValues.directPercentVotingRights || 0) +
            Number(beneficialOwnershipValues.indirectPercentVotingRights || 0),
        directPercentShareholding:
          beneficialOwnershipValues.directPercentShareholding || "",
        indirectPercentShareholding:
          beneficialOwnershipValues.indirectPercentShareholding || "",
        directPercentVotingRights:
          beneficialOwnershipValues.directPercentVotingRights || "",
        indirectPercentVotingRights:
          beneficialOwnershipValues.indirectPercentVotingRights || "",
        directRightRemoveDirector:
          beneficialOwnershipValues.directRightRemoveDirector || "no",
        indirectRightRemoveDirector:
          beneficialOwnershipValues.indirectRightRemoveDirector || "no",
        directCompanyControlRight:
          beneficialOwnershipValues.directCompanyControlRight || "no",
        indirectCompanyControlRight:
          beneficialOwnershipValues.indirectCompanyControlRight || "no",
        dateOfBecomingBO: beneficialOwnershipValues.dateOfBecomingBO || "",
        sourceOfBOInformation:
          beneficialOwnershipValues.sourceOfBOInformation || "",
        resolvedFields: beneficialOwnershipValues.resolvedFields || [],
      };

      // Track which fields have changed
      const beneficialOwnershipChanges = {};
      Object.keys(updatedBeneficialOwnership).forEach((key) => {
        if (
          String(currentBeneficialOwnership[key]) !==
          String(updatedBeneficialOwnership[key])
        ) {
          beneficialOwnershipChanges[key] = {
            from: currentBeneficialOwnership[key],
            to: updatedBeneficialOwnership[key],
          };
        }
      });

      // Build beneficial ownership details structure (matching ShareChangesSubRow format)
      const beneficialOwnershipDetails = {
        currentDetails: {
          directPercentShareholding: Number(
            currentBeneficialOwnership.directPercentShareholding || 0
          ),
          indirectPercentShareholding: Number(
            currentBeneficialOwnership.indirectPercentShareholding || 0
          ),
          directPercentVotingRights: Number(
            currentBeneficialOwnership.directPercentVotingRights || 0
          ),
          indirectPercentVotingRights: Number(
            currentBeneficialOwnership.indirectPercentVotingRights || 0
          ),
          directRightRemoveDirector:
            currentBeneficialOwnership.directRightRemoveDirector,
          indirectRightRemoveDirector:
            currentBeneficialOwnership.indirectRightRemoveDirector,
          directCompanyControlRight:
            currentBeneficialOwnership.directCompanyControlRight,
          indirectCompanyControlRight:
            currentBeneficialOwnership.indirectCompanyControlRight,
          dateOfBecomingBO: currentBeneficialOwnership.dateOfBecomingBO
            ? new Date(currentBeneficialOwnership.dateOfBecomingBO)
            : null,
          sourceOfBOInformation:
            currentBeneficialOwnership.sourceOfBOInformation,
          maxPercentShareholding:
            currentBeneficialOwnership.maxPercentShareholding,
          maxPercentVotingRights:
            currentBeneficialOwnership.maxPercentVotingRights,
        },
        updatedDetails: {
          directPercentShareholding: Number(
            updatedBeneficialOwnership.directPercentShareholding || 0
          ),
          indirectPercentShareholding: Number(
            updatedBeneficialOwnership.indirectPercentShareholding || 0
          ),
          directPercentVotingRights: Number(
            updatedBeneficialOwnership.directPercentVotingRights || 0
          ),
          indirectPercentVotingRights: Number(
            updatedBeneficialOwnership.indirectPercentVotingRights || 0
          ),
          directRightRemoveDirector:
            updatedBeneficialOwnership.directRightRemoveDirector,
          indirectRightRemoveDirector:
            updatedBeneficialOwnership.indirectRightRemoveDirector,
          directCompanyControlRight:
            updatedBeneficialOwnership.directCompanyControlRight,
          indirectCompanyControlRight:
            updatedBeneficialOwnership.indirectCompanyControlRight,
          dateOfBecomingBO: updatedBeneficialOwnership.dateOfBecomingBO
            ? new Date(updatedBeneficialOwnership.dateOfBecomingBO)
            : null,
          sourceOfBOInformation:
            updatedBeneficialOwnership.sourceOfBOInformation,
          maxPercentShareholding:
            updatedBeneficialOwnership.maxPercentShareholding,
          maxPercentVotingRights:
            updatedBeneficialOwnership.maxPercentVotingRights,
        },
        changedFields: beneficialOwnershipChanges,
        lastUpdated: new Date(),
        updatedBy: currentUser?._id,
        effectiveDate: shareAllocationData?.effectiveDate
          ? new Date(shareAllocationData.effectiveDate)
          : new Date(),
      };

      // Create new allocation with embedded beneficial ownership details
      const newAllocation = {
        officialId,
        name: fullNameForAlloc,
        currentShares,
        votingRights,
        allocatedShares,
        proportion,
        paidUpShares: Number(shareAllocationData.paidUpShares || 0),
        sharePrice: Number(shareAllocationData.sharePrice || 0),
        sharePremium: Number(shareAllocationData.sharePremium || 0),
        beneficialOwnershipDetails: beneficialOwnershipDetails,
      };

      // Update or add allocation
      if (idx >= 0) {
        newAllocations[idx] = newAllocation;
      } else {
        newAllocations.push(newAllocation);
      }

      // Calculate updated remainder
      const newAllocatedTotal = newAllocations.reduce(
        (sum, a) => sum + Number(a.allocatedShares || 0),
        0
      );
      const onOffer = Number(change?.sharesOnOffer || change?.onOffer || 0);
      const updatedRemainder = Math.max(0, onOffer - newAllocatedTotal);

      // Update the change with new allocations
      const updatedChange = {
        ...change,
        allocations: newAllocations,
        remainder: updatedRemainder,
      };

      const updatedShareCapitalChanges = changes.map((c, i) =>
        i === targetIdx ? updatedChange : c
      );

      // Prepare payload for transaction update
      const updatePayload = {
        companyId: company._id,
        id: matchingTransaction._id,
        documentId: matchingTransaction._id,
        formName: "initiateTransactionChange",
        shareCapitalChanges: updatedShareCapitalChanges,
      };
      const editResult = await handleCompanyMaintenanceFormData(
        "existing-request",
        updatePayload,
        company._id
      );

      const ok =
        editResult?.type ===
          "transactionChange/editInitiatedTransactionChangeAsync/fulfilled" ||
        editResult?.payload?.status === true;

      if (!ok) {
        const msg =
          editResult?.payload?.messageText ||
          editResult?.payload?.message ||
          "Failed to update transaction.";
        throw new Error(msg);
      }

      // Refresh transaction data
      await dispatch(
        getAllInitiateTransactionChangeAsync({
          companyId: company._id,
          params: { transactionType: "MANUAL" },
        })
      );

      // Calculate and display percentage changes
      calculateAndDisplayPercentageChanges();

      // Reset states
      setShareAllocationComplete(true);
      setAllocateSharesBtn(true);
    } catch (error) {
      console.error("❌ Error processing allotment for changes list:", error);
      // Show error message to user if toast is available
    } finally {
      setIsProcessingAllotment(false);
    }
  };
  const handleProcessAllotment = async (values) => {
    setIsProcessingAllotment(true);

    try {
      const beneficialOwnershipValues = values.beneficialOwnershipForm || {};

      // Calculate max percentages from direct + indirect
      const maxPercentShareholding =
        Number(beneficialOwnershipValues.directPercentShareholding || 0) +
        Number(beneficialOwnershipValues.indirectPercentShareholding || 0);
      const maxPercentVotingRights =
        Number(beneficialOwnershipValues.directPercentVotingRights || 0) +
        Number(beneficialOwnershipValues.indirectPercentVotingRights || 0);

      // Prepare payload with shares and beneficial ownership
      const payload = {
        companyId: company._id,
        idType: row.original.idType,
        sharesAllotted: shares, // From Redux state (updated by ShareAllocationSelection)
        beneficialOwnershipForm: {
          ...row.original.beneficialOwnershipForm,
          ...beneficialOwnershipValues,
          // Include calculated max percentages
          maxPercentShareholding,
          maxPercentVotingRights,
        },
      };

      // Save to database
      const result = await dispatch(
        updateCompanyOfficialAsync({
          officialId: row.original._id,
          data: payload,
        })
      );

      if (updateCompanyOfficialAsync.fulfilled.match(result)) {
        // Calculate and display percentage changes
        const percentageChanges = calculateAndDisplayPercentageChanges();

        // Reset states
        setShareAllocationComplete(true);
        setAllocateSharesBtn(true);

        // Show success message
      } else {
        throw new Error(
          result.error?.message || "Failed to update company official"
        );
      }
    } catch (error) {
      console.error("❌ Error processing allotment:", error);
      // Show error message to user if toast is available
    } finally {
      setIsProcessingAllotment(false);
    }
  };
  const calculateAndDisplayPercentageChanges = () => {
    try {
      // Get current shareholders (those with shares allotted)
      const currentShareholders = getShareholdersFromOfficials(
        originalCompanyOfficials,
        nominalShareCapital
      );

      // Split shareholders: those in current appointments vs changes list
      const currentAppointmentsShareholders = currentShareholders.filter(
        (sh) => !sh.isInChangesList
      );
      const changesListShareholders = currentShareholders.filter(
        (sh) => sh.isInChangesList
      );

      // Calculate original percentages (before any allocation)
      const originalPercentages = calculateShareholdingPercentagesCrossTable(
        currentAppointmentsShareholders,
        [], // Empty changes list for original state
        nominalShareCapital
      );

      // Calculate new percentages (after allocation)
      const newPercentages = calculateShareholdingPercentagesCrossTable(
        currentAppointmentsShareholders,
        changesListShareholders,
        nominalShareCapital
      );

      // Compare and get changes
      const percentageChanges = compareShareholdingPercentages(
        originalPercentages,
        newPercentages
      );

      setLocalPercentageChanges(percentageChanges);
      setShowPercentageAlert(Object.keys(percentageChanges).length > 0);

      // Notify parent component
      if (
        onPercentageChangesUpdate &&
        Object.keys(percentageChanges).length > 0
      ) {
        onPercentageChangesUpdate(percentageChanges);
      }

      return percentageChanges;
    } catch (error) {
      console.error("❌ Error calculating percentage changes:", error);
      return null;
    }
  };

  // approve/disapprove allotted shares to official
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRows, setSelectedRow] = useState([]);
  const [allFieldsResolved, setAllFieldsResolved] = useState(false);

  const isReviewerMode =
    history.location.pathname.split("/").pop() === "review";
  const isReviewer = Cookies.get("role") === Constants.COOKIE_ROLES.REVIEWER;

  const { shares: nominalShareCapital } = useSelector(
    (state) => state.sharesTableSlice
  );
  const { shares } = useSelector((state) => state.sharesAllocationTableSlice);

  const alreadyTakenClassOfShares = shares.map((share) => share.classOfShares);

  let classOfSharesOptions = useMemo(() => {
    return nominalShareCapital.map(({ sharesCategory }) => ({
      label: sharesCategory,
      value: sharesCategory,
    }));
  }, [nominalShareCapital]);

  const [selectedClassOfShare, setSelectedClassOfShare] = useState("");

  useEffect(() => {
    if (editableShareAlloc?.classOfShares) {
      setSelectedClassOfShare(
        classOfSharesOptions.find(
          (classOfShares) =>
            classOfShares.value === editableShareAlloc?.classOfShares
        )
      );
    }
  }, [editableShareAlloc?.classOfShares]); // eslint-disable-line react-hooks/exhaustive-deps
  // classOfSharesOptions removed - it's derived from nominalShareCapital which is stable

  const formId = _get(row.original, "_id");

  const fieldsName = row.original?.sharesAllotted
    ?.map((share) => share.classOfShares)
    .filter(Boolean); // all the shares category names

  const resolvedFields = _get(row.original, "resolvedFields", []);

  // Create stable keys for array dependencies
  const fieldsNameKey = useMemo(
    () => fieldsName?.join(",") || "",
    [fieldsName]
  );
  const resolvedFieldsKey = useMemo(
    () => resolvedFields?.join(",") || "",
    [resolvedFields]
  );

  useEffect(() => {
    setAllFieldsResolved(
      fieldsName?.every((element) => resolvedFields.includes(element))
    );
  }, [fieldsNameKey, resolvedFieldsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveComment = async (data) => {
    if (data?.commentId) {
      await dispatch(editComment({ commentId: data.commentId, data }));
    } else {
      await dispatch(
        addComment({
          data: {
            ...data,
            company: company._id,
            officialId: row.original._id,
            tabName: currentTab,
          },
        })
      );
    }
  };

  const officialShareAllocationColumns = [
    {
      Header: "Class of shares",
      Cell: ({ row }) => row.original.classOfShares,
    },
    { Header: "Number of Shares", accessor: "allottedShares" },
    {
      Header: "Value Of Shares",
      Cell: ({ row }) => (
        <div className="inline-flex items-baseline">
          <span className="px-1 text-xs font-medium">KES</span>{" "}
          <Tag>{row.original.sharesValue}</Tag>
        </div>
      ),
    },
    { Header: "Paid Up Shares", accessor: "paidUpShares" },
    {
      Header: "Premium Paid",
      Cell: ({ row }) => (
        <div className="inline-flex items-baseline">
          <span className="px-1 text-xs font-medium">KES</span>{" "}
          <Tag>{row.original.sharePremium}</Tag>
        </div>
      ),
    },
    {
      Header: "Nominal Value",
      Cell: ({ row }) => (
        <div className="inline-flex items-baseline">
          <span className="px-1 text-xs font-medium">KES</span>{" "}
          <Tag>{row.original.nominalValue}</Tag>
        </div>
      ),
    },
    {
      Header: "Voting Rights",
      Cell: ({ row }) => row.original.votingRights,
    },
  ];

  const handleShareEdit = (row) => {
    setEditableShareAlloc(row);
  };

  const handleShareDelete = async (shareRow) => {
    setEditableShareAlloc(null);
    await dispatch(
      updateCompanyOfficialAsync({
        officialId: row.original._id,
        data: {
          companyId: company._id,
          idType: row.original.idType,
          sharesAllotted: shares.filter((share) => share._id !== shareRow._id),
          beneficialOwnershipForm: {
            ...row.original.beneficialOwnershipForm,
            directPercentShareholding: "",
            indirectPercentShareholding: "",
            directPercentVotingRights: "",
            indirectPercentVotingRights: "",
            dateOfBecomingBO: "",
          },
        },
      })
    );

    // Recalculate percentage changes after deletion

    const updatedShares = shares.filter((share) => share._id !== shareRow._id);

    // If all shares are deleted, clear the percentage changes
    if (updatedShares.length === 0) {
      setLocalPercentageChanges(null);
      setShowPercentageAlert(false);

      // Notify parent to clear the warning icon
      if (onPercentageChangesUpdate) {
        onPercentageChangesUpdate(null);
      }
    } else {
      // Recalculate with remaining shares
      calculateAndDisplayPercentageChanges();
    }
  };

  if (!isViewMode) {
    officialShareAllocationColumns.push({
      Header: "Action",
      Cell: ({ row }) => {
        const isRowLoading =
          isLoading && selectedRows.includes(row.original.classOfShares);
        return (
          <div className="flex items-center space-x-2">
            <Button
              className="items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:cursor-not-allowed"
              onClick={() => handleShareEdit(row.original)}
              disabled={resolvedFields.includes(row.original.classOfShares)}
            >
              Edit
            </Button>
            <DeleteButtonWithConfirmation
              confirmationModalName="delete_share_allocation_confirmation_modal"
              row={row.original}
              disabled={resolvedFields.includes(row.original.classOfShares)}
              btnClasses="items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded text-red-700 bg-red-100 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:cursor-not-allowed"
            />
            {!resolvedFields.includes(row.original.classOfShares) && (
              <CommentsPopup
                handleSaveComment={handleSaveComment}
                formName="officialShareAllocation"
                fieldName={row.original.classOfShares}
                fieldLabel={row.original.classOfShares}
                comment={officialShareAllocationComments.find(
                  (comment) =>
                    comment.fieldName === row.original.classOfShares &&
                    currentUser?._id === comment.createdBy?._id
                )}
              >
                <span>Review</span>
                <PencilIcon className="inline w-4 h-4" />
              </CommentsPopup>
            )}
            <div className={`flex items-center justify-center pr-4`}>
              {resolvedFields.includes(row.original.classOfShares) ? (
                <button
                  className={`flex space-x-1 ${
                    isRowLoading ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    isRowLoading
                      ? null
                      : handleInvalidateBtnClicked(row.original.classOfShares)
                  }
                >
                  {isRowLoading ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accepted </span>
                      <XIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              ) : (
                <button
                  className={`flex space-x-1 ${
                    isRowLoading ? "cursor-not-allowed" : "cursor-pointer"
                  }`}
                  onClick={() =>
                    isRowLoading
                      ? null
                      : handleResolveBtnClicked(row.original.classOfShares)
                  }
                >
                  {isRowLoading ? (
                    <SpinnerIcon className="text-primary" />
                  ) : (
                    <>
                      <span>Accept</span>
                      <CheckIcon className="inline w-4 h-4" />
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        );
      },
    });
  }

  const handleResolveBtnClicked = async (fieldName) => {
    // approve field
    const commentsToBeResolved = officialShareAllocationComments?.filter(
      (comment) => comment.fieldName === fieldName && !comment.isResolved
    );
    const commentsToBeResolvedIds = commentsToBeResolved?.map(({ _id }) => _id);
    setIsLoading(true);
    setSelectedRow((selectedRows) => [...selectedRows, fieldName]);
    const promise = await dispatch(
      resolveFields({
        data: {
          formId,
          formName:
            currentTab === "stakeholderTab"
              ? INDIVIDUALS.includes(row?.original?.idType)
                ? "individualOfficial"
                : "corporateOfficial"
              : "beneficialOwner",
          fieldName,
          resolvedFields: [fieldName],
          commentsToBeResolvedIds,
        },
        updateEffectKey: companyOfficialEffectKey,
        updateEffectPath: officialSliceName,
      })
    );
    if (commentsToBeResolvedIds.length > 0) {
      const query = { companyId: commentsToBeResolved[0]?.company };
      if (isReviewerMode) query.createdBy = currentUser?._id;
      await dispatch(getComments(query));
    }
    setIsLoading(false);
    setSelectedRow((selectedRows) => [
      ...selectedRows.filter(
        (rowName) => rowName !== promise.meta.arg.data.fieldName
      ),
    ]);
  };

  const handleInvalidateBtnClicked = async (fieldName) => {
    // disapprove field
    setIsLoading(true);
    setSelectedRow((selectedRows) => [...selectedRows, fieldName]);
    const promise = await dispatch(
      invalidateFields({
        data: {
          formId,
          formName:
            currentTab === "stakeholderTab"
              ? INDIVIDUALS.includes(row?.original?.idType)
                ? "individualOfficial"
                : "corporateOfficial"
              : "beneficialOwner",
          fieldName,
          invalidFields: [fieldName],
        },
        updateEffectKey: companyOfficialEffectKey,
        updateEffectPath: officialSliceName,
      })
    );
    setIsLoading(false);
    setSelectedRow((selectedRows) => [
      ...selectedRows.filter(
        (rowName) => rowName !== promise.meta.arg.data.fieldName
      ),
    ]);
  };

  const handleAllFieldsStatus = async (e) => {
    const commentsToBeResolved = officialShareAllocationComments?.filter(
      (comment) => !comment.isResolved
    );
    const commentsToBeResolvedIds = commentsToBeResolved?.map(({ _id }) => _id);
    setIsLoading(true);
    if (e.target.checked) {
      // resolve/approve all the form fields
      const unresolvedFields = fieldsName.filter(
        (fieldName) => !resolvedFields.includes(fieldName)
      );
      setSelectedRow((selectedRows) => [...selectedRows, ...unresolvedFields]);
      await dispatch(
        resolveFields({
          data: {
            formId,
            formName:
              currentTab === "stakeholderTab"
                ? INDIVIDUALS.includes(row?.original?.idType)
                  ? "individualOfficial"
                  : "corporateOfficial"
                : "beneficialOwner",
            resolvedFields: unresolvedFields,
            commentsToBeResolvedIds,
          },
          updateEffectKey: companyOfficialEffectKey,
          updateEffectPath: officialSliceName,
        })
      );
      if (commentsToBeResolvedIds.length > 0) {
        const query = { companyId: commentsToBeResolved[0]?.company };
        if (isReviewerMode) query.createdBy = currentUser?._id;
        await dispatch(getComments(query));
      }
      setIsLoading(false);
      setSelectedRow([]);
    } else {
      // invalidate/disapprove all the form fields
      const resolvedFieldsSlice = fieldsName.filter((fieldName) =>
        resolvedFields.includes(fieldName)
      );
      setSelectedRow((selectedRows) => [
        ...selectedRows,
        ...resolvedFieldsSlice,
      ]);
      await dispatch(
        invalidateFields({
          data: {
            formId,
            formName:
              currentTab === "stakeholderTab"
                ? INDIVIDUALS.includes(row?.original?.idType)
                  ? "individualOfficial"
                  : "corporateOfficial"
                : "beneficialOwner",
            invalidFields: resolvedFieldsSlice,
          },
          updateEffectKey: companyOfficialEffectKey,
          updateEffectPath: officialSliceName,
        })
      );
      setSelectedRow([]);
    }
    setIsLoading(false);
  };

  const showShareAllocationSelection =
    !showAllocateSharesBtn || editableShareAlloc;

  const availableClassOfSharesOptions = classOfSharesOptions.filter(
    (option) => !alreadyTakenClassOfShares.includes(option.value)
  );

  // Helper function to extract shares from transactions (same as main table)
  // MOVED BEFORE CONDITIONAL RETURN to comply with React Hooks rules
  // Wrapped in useCallback to avoid recreation on every render
  const getSharesFromTransactions = useCallback(
    (officialId, shareCapitalChanges, nominalShareCapital = []) => {
      if (!officialId || !Array.isArray(shareCapitalChanges)) {
        return [];
      }

      const sharesByClass = [];

      shareCapitalChanges.forEach((transaction) => {
        if (transaction.allocations && Array.isArray(transaction.allocations)) {
          const officialAllocation = transaction.allocations.find(
            (allocation) => String(allocation.officialId) === String(officialId)
          );

          if (officialAllocation && transaction.classOfShares) {
            const allocatedShares = Number(
              officialAllocation.allocatedShares || 0
            );
            const classOfShares = transaction.classOfShares;

            if (allocatedShares > 0) {
              // Lookup nominal share details for votesPerShare
              const nominalShare = nominalShareCapital.find(
                (ns) =>
                  ns.sharesCategory === classOfShares ||
                  ns._id === transaction.nominalShareId
              ) || { votesPerShare: 1 }; // Fallback to 1 vote per share

              // Per-share values
              const sharesValue =
                Number(transaction.offerPricePerShare || 0) -
                Number(transaction.premiumPerShare || 0);
              const sharePrice = Number(transaction.offerPricePerShare || 0);
              const sharePremium = Number(transaction.premiumPerShare || 0);
              const votingRights = allocatedShares * nominalShare.votesPerShare;

              // Create a share allocation object in the expected format
              sharesByClass.push({
                id: `${transaction._id}-${officialAllocation._id}`, // Unique ID for table row
                classOfShares: classOfShares,
                allottedShares: allocatedShares,
                sharesValue, // Per-share nominal value (e.g., 120 or 100)
                paidUpShares:
                  Number(officialAllocation.paidUpShares || 0) ||
                  allocatedShares, // Override to allotted if 0
                sharePrice,
                sharePremium,
                nominalValue: allocatedShares * sharesValue, // Total nominal (e.g., 25 * 120 = 3000)
                votingRights,
                // Optional: Preserve additional fields
                effectiveDate: transaction.dateOfOffer,
                beneficialOwnershipDetails:
                  officialAllocation.beneficialOwnershipDetails,
              });
            }
          }
        }
      });

      return sharesByClass;
    },
    []
  ); // No dependencies - function logic is pure

  // For changes list, extract data from transactions using useMemo to ensure fresh data
  const { changesListData, transactionBODetails } = useMemo(() => {
    if (!isChangesList) {
      return { changesListData: [], transactionBODetails: null };
    }

    // Create data structure from transaction changes
    let changesListData = [];

    // First try to extract shareCapitalChanges from transactions context
    let shareCapitalChanges = [];
    if (transactions && Array.isArray(transactions)) {
      // Aggregate shareCapitalChanges from all transactions
      transactions.forEach((transaction) => {
        if (
          transaction.shareCapitalChanges &&
          Array.isArray(transaction.shareCapitalChanges)
        ) {
          shareCapitalChanges = shareCapitalChanges.concat(
            transaction.shareCapitalChanges
          );
        }
      });
    }

    // Use the new function to extract shares data
    if (shareCapitalChanges.length > 0 && row.original._id) {
      changesListData = getSharesFromTransactions(
        row.original._id,
        shareCapitalChanges
      );
    }

    // Extract beneficial ownership details from transaction
    const getTransactionBeneficialOwnershipDetails = () => {
      setRefechTransactionsOnUpdate(true);
      if (!transactions || !Array.isArray(transactions)) return null;

      // Find the transaction that matches this row's transaction ID
      const matchingTransaction = transactions.find(
        (tx) =>
          tx._id === row.original.transaction || tx._id === row.original._id
      );

      if (!matchingTransaction?.shareCapitalChanges) return null;

      for (const shareChange of matchingTransaction.shareCapitalChanges) {
        if (shareChange.allocations && Array.isArray(shareChange.allocations)) {
          const allocation = shareChange.allocations.find(
            (alloc) => String(alloc.officialId) === String(row.original._id)
          );
          if (allocation?.beneficialOwnershipDetails?.updatedDetails) {
            const updatedDetails =
              allocation.beneficialOwnershipDetails.updatedDetails;
            const mappedBODetails = {
              // Map the updated details to the flat structure expected by OfficialsBeneficialOwnershipComponent
              // Use 0 for empty percentage fields instead of empty string
              directPercentShareholding:
                updatedDetails.directPercentShareholding ?? 0,
              indirectPercentShareholding:
                updatedDetails.indirectPercentShareholding ?? 0,
              directPercentVotingRights:
                updatedDetails.directPercentVotingRights ?? 0,
              indirectPercentVotingRights:
                updatedDetails.indirectPercentVotingRights ?? 0,
              directRightRemoveDirector:
                updatedDetails.directRightRemoveDirector || "no",
              indirectRightRemoveDirector:
                updatedDetails.indirectRightRemoveDirector || "no",
              directCompanyControlRight:
                updatedDetails.directCompanyControlRight || "no",
              indirectCompanyControlRight:
                updatedDetails.indirectCompanyControlRight || "no",
              dateOfBecomingBO: updatedDetails.dateOfBecomingBO || "",
              sourceOfBOInformation: updatedDetails.sourceOfBOInformation || "",
              maxPercentShareholding:
                updatedDetails.maxPercentShareholding ?? 0,
              maxPercentVotingRights:
                updatedDetails.maxPercentVotingRights ?? 0,
              // Preserve resolvedFields from original data if available
              resolvedFields:
                row.original.beneficialOwnershipForm?.resolvedFields || [],
            };

            return mappedBODetails;
          }
        }
      }

      return null;
    };

    let transactionBODetails = getTransactionBeneficialOwnershipDetails();

    // Fallback to old logic only if no data was extracted from transactions
    if (changesListData.length === 0) {
      if (
        row.original.allocatedShares !== undefined &&
        row.original.classOfShares
      ) {
        // For individual row changes (when row has transaction-specific data)

        const allocatedShares = Number(row.original.allocatedShares || 0);

        const changeItem = {
          classOfShares: row.original.classOfShares,
          allottedShares: row.original.allocatedShares,
          offerPricePerShare: row.original.offerPricePerShare || "0.00",
          sharePremium: row.original.premiumPerShare || "0.00", // Premium paid
          premiumPerShare: row.original.premiumPerShare || "0.00", // Fallback property
          paidUpShares: row.original.allocatedShares, // Assuming fully paid
          votingRights: allocatedShares, // Use number of shares for voting rights
          sharesValue:
            allocatedShares * (Number(row.original.offerPricePerShare) || 0),
          nominalValue:
            allocatedShares *
            (Number(row.original.AmountCreditedAsPaidUpCapitalPerShare) || 0),
        };
        changesListData.push(changeItem);
      } else if (row.original.sharesAllotted?.length > 0) {
        // Fallback to original shares allotted if transaction data not available

        changesListData.push(...row.original.sharesAllotted);
      }
    }
    return {
      changesListData,
      transactionBODetails,
    };
  }, [
    isChangesList,
    transactions,
    row,
    transactionDataVersion,
    getSharesFromTransactions,
  ]); // Re-run when dependencies change

  // Early return for share allocation selection UI (before changes list check)
  if (showShareAllocationSelection) {
    return (
      <div className="px-8 pt-5 pb-6 my-6 bg-white">
        <ShareAllocationSelection
          companyId={company._id}
          setAllocateSharesBtn={setAllocateSharesBtn}
          editableShareAlloc={editableShareAlloc}
          setEditableShareAlloc={setEditableShareAlloc}
          currentOfficial={row.original}
          alreadyTakenClassOfShares={alreadyTakenClassOfShares}
          selectedClassOfShare={selectedClassOfShare}
          setSelectedClassOfShare={setSelectedClassOfShare}
          remainderAmounts={remainderAmounts}
          isChangesList={isChangesList}
          onSubmitSuccess={(updatedShares) => {
            // Mark allocation as complete
            setShareAllocationComplete(true);

            // Calculate and display percentage changes
            calculateAndDisplayPercentageChanges();
          }}
        />
      </div>
    );
  }

  // For changes list, render the UI
  if (isChangesList) {
    return (
      <div className="mt-4">
        {officialShareAllocationComments &&
          officialShareAllocationComments.length > 0 && (
            <div className="px-8 pt-5 pb-6 my-6 bg-white">
              <CommentsList
                comments={officialShareAllocationComments}
                toggleCommentStatus
                applicantReviewer
              />
              <p className="mt-6 text-sm text-primary">
                Comments: {officialShareAllocationComments.length} of{" "}
                {officialShareAllocationComments.length}
              </p>
            </div>
          )}
        {changesListData.length > 0 ? (
          <div>
            <h3 className="mb-4 text-md font-semibold">New Allotted Shares</h3>

            {/* Share Allocation Component for Changes List with Edit Functionality */}
            <div className="mb-6">
              <Formik
                enableReinitialize
                initialValues={{
                  sharesAllotted: changesListData,
                  beneficialOwnershipForm:
                    transactionBODetails ||
                    row.original.beneficialOwnershipForm ||
                    {},
                }}
                onSubmit={() => {}}
              >
                {(formikProps) => {
                  // Update the form values with changesListData to ensure it's available
                  if (formikProps.values.sharesAllotted !== changesListData) {
                    formikProps.setFieldValue(
                      "sharesAllotted",
                      changesListData
                    );
                  }

                  return (
                    <MainShareAllocationComponent
                      currentOfficialId={row.original._id}
                      isCorporate={!INDIVIDUALS.includes(row?.original?.idType)}
                      resolvedFields={[]} // No resolved fields in changes list
                      disabled={isViewMode} // Allow editing based on view mode
                      remainderAmounts={remainderAmounts}
                      transactions={transactions}
                      row={row}
                      editableShareAlloc={null}
                      autoOpenSelection={false}
                      onSubmissionComplete={() => {}}
                      isInChangesList={isChangesList}
                      onEditManualAllocation={() => {}}
                      onClearManualAllocation={() => {}} // Disable clear functionality
                      showEditOnly={true} // Custom prop to show only edit, no delete/clear
                      changesListData={changesListData} // Pass the changes list data
                    />
                  );
                }}
              </Formik>
            </div>

            {/* Beneficial Ownership Component below the table - EDITABLE with Process Allotment */}
            <div className="mt-6">
              <div className="bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
                <div className="px-4 py-6 sm:p-6">
                  <h3 className="mb-4 text-lg font-semibold">
                    Beneficial Ownership Details
                  </h3>
                  <Formik
                    enableReinitialize
                    initialValues={{
                      beneficialOwnershipForm:
                        transactionBODetails ||
                        row.original.beneficialOwnershipForm ||
                        {},
                    }}
                    onSubmit={(values) => {
                      // Use changes list handler for changes list, regular handler for current appointments
                      if (isChangesList) {
                        handleProcessAllotmentForChangesList(values);
                      } else {
                        handleProcessAllotment(values);
                      }
                    }}
                  >
                    {(formikProps) => {
                      return (
                        <>
                          <OfficialsBeneficialOwnershipComponent
                            field={{
                              name: "beneficialOwnershipForm",
                              value: formikProps.values.beneficialOwnershipForm,
                            }}
                            form={formikProps}
                            corporateIdType={
                              !INDIVIDUALS.includes(row?.original?.idType)
                            }
                            isBOTab={false}
                            disableChangeEffect={false}
                            selectedShareholder={{
                              ...row.original,
                              // Pass changesListData as sharesAllotted for percentage calculation
                              sharesAllotted: changesListData,
                            }}
                            disableDirectFields={false}
                            isReportable={true}
                            isInManualUpdate={true}
                            isinChangesList={true}
                            transactions={transactions}
                            row={{
                              ...row,
                              original: {
                                ...row.original,
                                // Pass changesListData as sharesAllotted for percentage calculation
                                sharesAllotted: changesListData,
                              },
                            }}
                            disabled={isViewMode}
                            percentageChanges={localPercentageChanges}
                            originalPercentages={null}
                          />

                          {/* Process Allotment Button for Changes List */}
                          {!isViewMode && (
                            <div className="flex justify-end mt-6">
                              <Button
                                onClick={formikProps.handleSubmit}
                                isLoading={isProcessingAllotment}
                                loadingText="Processing..."
                                className="px-4 py-2 text-sm font-medium text-white rounded bg-sflPrimary hover:bg-optional"
                                disabled={isProcessingAllotment}
                              >
                                Process Allotment
                              </Button>
                            </div>
                          )}
                        </>
                      );
                    }}
                  </Formik>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <div className="bg-white shadow p-4">
              <p className="text-center text-gray-500">
                No share allocation data available for this change
              </p>
            </div>

            {/* Beneficial Ownership Component below the no-data message */}
            <div className="mt-6">
              <h3 className="mb-4 text-lg font-semibold">
                Beneficial Ownership Details
              </h3>
              <Formik
                enableReinitialize
                initialValues={{
                  beneficialOwnershipForm:
                    transactionBODetails ||
                    row.original.beneficialOwnershipForm ||
                    {},
                }}
                onSubmit={() => {}}
              >
                {(formikProps) => {
                  return (
                    <OfficialsBeneficialOwnershipComponent
                      field={{
                        name: "beneficialOwnershipForm",
                        value:
                          transactionBODetails ||
                          row.original.beneficialOwnershipForm ||
                          {},
                      }}
                      form={formikProps}
                      corporateIdType={
                        !INDIVIDUALS.includes(row?.original?.idType)
                      }
                      isBOTab={false}
                      disableChangeEffect={true}
                      selectedShareholder={row.original}
                      disableDirectFields={false}
                      isReportable={false}
                      isInManualUpdate={false}
                      transactions={transactions}
                      row={row}
                      disabled={isViewMode}
                      resolvedFields={_get(
                        row.original.beneficialOwnershipForm,
                        "resolvedFields",
                        []
                      )}
                    />
                  );
                }}
              </Formik>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="mt-4">
      <ConfirmDialog
        name="delete_share_allocation_confirmation_modal"
        onConfirm={() => handleShareDelete(modalRow)}
      >
        Are you sure you want to permanently delete this entry?
      </ConfirmDialog>
      {availableClassOfSharesOptions.length > 0 && (
        <Button
          className="mb-2"
          disabled={availableClassOfSharesOptions.length === 0}
          onClick={() => {
            setAllocateSharesBtn(false);
          }}
        >
          Allot Shares
        </Button>
      )}
      {officialShareAllocationComments &&
        officialShareAllocationComments.length > 0 && (
          <div className="px-8 pt-5 pb-6 my-6 bg-white">
            <CommentsList
              comments={officialShareAllocationComments}
              toggleCommentStatus
              applicantReviewer
            />
            <p className="mt-6 text-sm text-primary">
              Comments: {officialShareAllocationComments.length} of{" "}
              {officialShareAllocationComments.length}
            </p>
          </div>
        )}
      {row.original.sharesAllotted?.length > 0 || shareAllocationComplete ? (
        <>
          <div className="bg-white shadow overflow-auto">
            <ReactTable
              columns={officialShareAllocationColumns}
              data={
                shareAllocationComplete ? shares : row.original.sharesAllotted
              }
            />
          </div>
          {!isReviewer && (
            <div className="flex items-center mt-2">
              <input
                aria-describedby="comments-description"
                type="checkbox"
                className="mr-2 text-indigo-600 border-grayw-300 rounded focus:ring-indigo-500"
                onChange={handleAllFieldsStatus}
                checked={allFieldsResolved}
              />
              <p className="flex items-center text-sm font-semibold">
                Click this checkbox to{" "}
                {allFieldsResolved ? "disapprove" : "approve"} all entries if
                they are {allFieldsResolved ? "incorrect" : "correct"} then
                proceed to next step
              </p>
            </div>
          )}

          {/* Beneficial Ownership Section - Show when shares are allocated */}
          {shareAllocationComplete && !isChangesList && (
            <div className="mt-6">
              <div className="bg-white rounded-sm shadow-sm sm:rounded md:rounded-md">
                <div className="px-4 py-6 sm:p-6">
                  <h2 className="mb-4 text-lg font-medium leading-6 text-gray-900">
                    Beneficial Ownership Distribution
                  </h2>
                  <p className="mb-4 text-sm text-gray-600">
                    Distribute the shareholding percentage between direct and
                    indirect ownership
                  </p>
                  <Formik
                    enableReinitialize
                    initialValues={{
                      beneficialOwnershipForm: {
                        maxPercentShareholding: 0,
                        maxPercentVotingRights: 0,
                        directPercentShareholding: "",
                        indirectPercentShareholding: "",
                        directPercentVotingRights: "",
                        indirectPercentVotingRights: "",
                        directRightRemoveDirector: "no",
                        indirectRightRemoveDirector: "no",
                        directCompanyControlRight: "no",
                        indirectCompanyControlRight: "no",
                        dateOfBecomingBO: "",
                        sourceOfBOInformation: "",
                        ...row.original.beneficialOwnershipForm,
                      },
                    }}
                    onSubmit={(values) => {
                      handleProcessAllotment(values.beneficialOwnershipForm);
                    }}
                  >
                    {(formikProps) => (
                      <>
                        <OfficialsBeneficialOwnershipComponent
                          field={{
                            name: "beneficialOwnershipForm",
                            value: formikProps.values.beneficialOwnershipForm,
                          }}
                          form={formikProps}
                          corporateIdType={
                            !INDIVIDUALS.includes(row?.original?.idType)
                          }
                          isBOTab={false}
                          disableChangeEffect={false}
                          selectedShareholder={row.original}
                          disableDirectFields={false}
                          isReportable={true}
                          isInManualUpdate={true}
                          transactions={transactions}
                          row={row}
                          disabled={isViewMode}
                          percentageChanges={localPercentageChanges}
                          originalPercentages={null}
                        />

                        {/* Process Allotment Button */}
                        <div className="flex justify-end mt-6 space-x-2">
                          <Button
                            onClick={() => {
                              setShareAllocationComplete(false);
                              setAllocateSharesBtn(true);
                            }}
                            className="px-4 py-2 text-sm font-medium text-white bg-gray-500 rounded hover:bg-gray-600"
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={formikProps.handleSubmit}
                            isLoading={isProcessingAllotment}
                            loadingText="Processing..."
                            className="px-4 py-2 text-sm font-medium text-white rounded bg-sflPrimary hover:bg-optional"
                            disabled={isProcessingAllotment}
                          >
                            Process Allotment
                          </Button>
                        </div>
                      </>
                    )}
                  </Formik>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <p className="mt-4 text-center">No data available</p>
      )}
    </div>
  );
}

export default OfficialShareAllocation;
